import { AuthenticationStatusGetStream, setAuthStatus, setAuthStatusPleb } from "../store/slices/authSlice";
import { setStatusGetStream } from "../store/slices/authSlice";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../store";
import { useAuth } from "./useAuth";
import { useCallback, useEffect } from "react";
import { AuthenticationStatus } from "../store/slices/authSlice";
import { useStream } from "./useStream";
import { Repository } from "../repositories/Repository";
import { useWallet } from "./useWallet";
import { useWalletTransactions } from "./useWalletTransactions";
import { useTheme } from "./useTheme";
import { currentUserRepository } from "../repositories/CurrentUserRepository";
import mixpanel from "mixpanel-browser";
import { useConfirm } from "./useConfirm";
import { ButtonVariant } from "../config/colors";
import { streamService } from "../utils/StreamService";
import { exchangeRateRepository } from "../repositories/ExchangeRateRepository";
import { getCurrencyCode } from "../utils/Utils";
import { Transaction, WalletType } from "../models/Transactions";
import { WalletMigrationStatus } from "../models/Pleb";
import { filtersUnreadStore } from "../pages/chat/components/filtersUnreadStore";
import { useBlockedUsers } from "./useBlockedUsers";

export function useApp() {
    const dispatch = useDispatch<AppDispatch>();
    const {pleb, status, currentUserId, tokens, logout} = useAuth();
    const {loginGetStream, logoutGetStream} = useStream();
    const {refreshBlockedUsers, resetBlockedUsers} = useBlockedUsers();
    const {initSparkWallet, initInternalWallet, unsetWallet, refreshBalance, setOnPaymentSucceededCallback, setOnSyncedCallback} = useWallet();
    const {refreshPendingTransactions, cleanupPendingTransactions, unsetWalletTransactions, refreshLastTransactions, markTransactionAsAnnounced} = useWalletTransactions();
    const {initializeTheme, isDarkMode} = useTheme();
    const {openConfirm} = useConfirm();

    useEffect(() => {
        initializeTheme();
    }, [initializeTheme]);

    useEffect(() => {
        exchangeRateRepository.preloadExchangeRateCache(getCurrencyCode());
    }, []);

    const handleVisibilityChange = useCallback(async () => {
        if (document.hidden) {
            //console.log('Tab hidden, reducing WebSocket activity');
        } else {
            //console.log('Tab visible, resuming normal WebSocket activity');
            
            try {
                await refreshBalance();
                await refreshLastTransactions();
            } catch (error) {
                console.error('Error refreshing data:', error);
            }
        }
    }, [refreshBalance, refreshLastTransactions]);

    useEffect(() => {
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [handleVisibilityChange]);

    useEffect(() => {
        const handlePaymentSucceeded = (transaction: Transaction) => {
            refreshBalance();
            refreshLastTransactions();
        };
        setOnPaymentSucceededCallback(handlePaymentSucceeded);
        return () => {
            setOnPaymentSucceededCallback(null);
        };
    }, [setOnPaymentSucceededCallback, refreshLastTransactions, markTransactionAsAnnounced]);

    useEffect(() => {
        const handleSynced = () => {
            console.log('wallet synced');
            refreshBalance();
            refreshLastTransactions();
        };
        setOnSyncedCallback(handleSynced);
        return () => {
            setOnSyncedCallback(null);
        };
    }, [setOnSyncedCallback, refreshLastTransactions]);

    useEffect(() => {
        refreshPendingTransactions();
        
        return () => {
            cleanupPendingTransactions();
        };
    }, [refreshPendingTransactions, cleanupPendingTransactions]);
    
    useEffect(() => {
        if (isDarkMode) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
    }, [isDarkMode]);

    const initCurrentUser = useCallback(async () => {
        Repository.onLogout = () => {
            logout();
        }

        if(tokens) {
            Repository.setCredentials(tokens);

        currentUserRepository.getProfile()
            .then((pleb) => {
                if(pleb.subscription)
                {
                    dispatch(setAuthStatusPleb({
                        status: AuthenticationStatus.AUTHENTICATED,
                        pleb: pleb?.toJson() ?? null,
                        tokens: tokens
                    }));
                    return;
                }
                else
                {
                    logout();
                    openConfirm({
                        message: "To use this app you must have an active subscription",
                        buttons: [
                            { 
                                label: "Sign up", 
                                href: import.meta.env.VITE_LINK_SIGNUP, 
                                target: "_blank", 
                                variant: ButtonVariant.primary 
                            },
                        ],
                    });
                }
            })
            .catch((error) => {
                console.error("error", error);
            });
        }
    }, [currentUserId, loginGetStream, dispatch]);

    useEffect(() => {
        switch(status) {
            case AuthenticationStatus.AUTHENTICATED:
                loginGetStream(pleb?.id ?? '')
                    .then(() => {
                        const getStreamUser = streamService.getChatClient().user;
                        
                        if(pleb?.id && pleb?.id === getStreamUser?.id) {
                            if(pleb?.name !== getStreamUser?.name || pleb?.thumbnail !== getStreamUser?.image) {
                                streamService.getChatClient().partialUpdateUsers([{
                                    id: pleb.id,
                                    set: {
                                        name: pleb?.name,
                                        image: pleb?.thumbnail
                                    }
                                }]);
                            }
                        }

                        dispatch(setStatusGetStream(AuthenticationStatusGetStream.LOGGED));
                    })
                    .catch((error) => {
                        console.error('error login GetStream', error);
                        dispatch(setStatusGetStream(AuthenticationStatusGetStream.ERROR));
                    });

                if(pleb) {
                    let walletToInit: WalletType | undefined = undefined;

                    if(pleb.walletType === WalletType.SPARK) {
                        walletToInit = WalletType.SPARK;
                    } else {
                        walletToInit = WalletType.INTERNAL;
                    }

                    if(pleb.walletMigrationStatus === WalletMigrationStatus.READY_TO_MIGRATE) {
                        walletToInit = WalletType.SPARK;
                    }

                    if(walletToInit) {
                        switch(walletToInit) {
                            case WalletType.SPARK:
                                initSparkWallet(pleb);
                                break;
                            case WalletType.INTERNAL:
                                if(tokens)
                                    initInternalWallet(pleb, tokens);
                                break;
                        }
                    }
                }

                mixpanel.identify(pleb?.id ?? '');
                mixpanel.people.set({
                    name: pleb?.name,
                    slug: pleb?.slug,
                    email: pleb?.email
                });
                mixpanel.track('$ae_session');
                refreshBlockedUsers();
                break;
            case AuthenticationStatus.LOGGING_OUT:
                resetBlockedUsers();
                Promise.all([
                    logoutGetStream(),
                    unsetWallet()
                ])
                    .then(() => {
                        filtersUnreadStore.clear();
                        unsetWalletTransactions();
                        dispatch(setAuthStatus(AuthenticationStatus.INITIAL));
                    })
                    .catch((error) => {
                        console.error('error logoutGetStream and unsetWallet', error);
                        dispatch(setAuthStatus(AuthenticationStatus.INITIAL));
                    })
                break;
            case AuthenticationStatus.INITIAL:
                if(currentUserId) {
                    initCurrentUser()
                }
                else {
                    dispatch(setAuthStatus(AuthenticationStatus.UNAUTHENTICATED));
                }
                break;
        }
    }, [status]);

    // to fix the height of the page on ios safari
    useEffect(() => {
        // Set CSS variable for viewport height
        const setVH = () => {
            const vh = window.innerHeight * 0.01;
            document.documentElement.style.setProperty('--vh', `${vh}px`);
        };
    
        // Initial set
        setVH();
    
        // Update on resize and orientation change
        window.addEventListener('resize', setVH);
        window.addEventListener('orientationchange', setVH);
    
        return () => {
        window.removeEventListener('resize', setVH);
        window.removeEventListener('orientationchange', setVH);
        };
    }, []);
}